import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { ASSETS, NAV, LINKS } from "../lib/constants";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  const scrollToTop = () => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true, force: true });
      requestAnimationFrame(() => window.__lenis.scrollTo(0, { immediate: true, force: true }));
    } else {
      window.scrollTo(0, 0);
    }
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-[100] transition-all duration-500 ${
          scrolled ? "border-b border-white/10 bg-ink/80 backdrop-blur-xl" : "border-b border-transparent bg-gradient-to-b from-ink/70 to-transparent"
        }`}
        data-testid="navbar"
      >
        <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 lg:px-12">
          <Link to="/" data-testid="nav-logo" onClick={scrollToTop} className="flex items-center gap-3">
            <img src={ASSETS.logo} alt="Higher Ground DC" className="h-20 w-auto object-contain md:h-28" />
          </Link>

          <div className="hidden items-center gap-8 xl:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `link-underline text-xs font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-white/70 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <a
              href={LINKS.waiver}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-waiver-cta"
              className="hidden border border-gold/50 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink lg:inline-block"
            >
              Safe Use Waiver
            </a>
            <a
              href={LINKS.menu}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-shop-cta"
              className="hidden bg-gold px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-champagne md:inline-block"
            >
              Shop Menu
            </a>
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="nav-menu-open"
              className="flex h-11 w-11 items-center justify-center border border-white/15 text-white transition-colors hover:border-gold hover:text-gold xl:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-[110] flex flex-col bg-ink"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <img src={ASSETS.logo} alt="Higher Ground DC" className="h-14 w-auto object-contain" />
              <button
                onClick={() => setMenuOpen(false)}
                data-testid="nav-menu-close"
                className="flex h-11 w-11 items-center justify-center border border-white/15 text-white transition-colors hover:border-gold hover:text-gold"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-1 flex-col justify-center gap-2 px-8">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i + 0.1, duration: 0.5 }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block font-display text-3xl font-light uppercase tracking-[0.15em] transition-colors ${
                        isActive ? "text-gold" : "text-white/80"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
            <div className="border-t border-white/10 p-8 space-y-3">
              <a
                href={LINKS.waiver}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-waiver-cta"
                className="block border border-gold/50 py-4 text-center text-xs font-semibold uppercase tracking-[0.25em] text-gold"
              >
                Safe Use Waiver
              </a>
              <a
                href={LINKS.menu}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-gold py-4 text-center text-xs font-semibold uppercase tracking-[0.25em] text-ink"
              >
                Shop Live Menu
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
